import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { TOOLTIP_STYLE, toKey, toNumber } from './chart-theme';
import { useChartAnimation } from './use-chart-animation';

export interface AreaSeries {
  key: string;
  label: string;
  color: string;
}

/**
 * Stacked-area history of a mix over time — how the *composition* moved, which
 * a set of separate lines makes much harder to read.
 *
 * Series stack in array order, so pass them in the same order as the current-
 * period chart above it; a legend shared between the two only works if the
 * bands sit in the same sequence.
 */
export function StackedAreaTrend({
  labels,
  series,
  rows,
  height = 150,
  unit = '%',
}: {
  labels: string[];
  series: AreaSeries[];
  /** One record per label, keyed by series key. */
  rows: Record<string, number>[];
  height?: number;
  unit?: string;
}) {
  const animation = useChartAnimation();
  const data = labels.map((label, i) => ({ label, ...rows[i] }));
  const labelFor = new Map(series.map((s) => [s.key, s.label]));

  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ top: 6, right: 6, bottom: 0, left: 0 }}>
          <XAxis
            dataKey="label"
            interval={1}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 9, fill: 'var(--muted-foreground)' }}
          />
          <YAxis hide />
          <Tooltip
            contentStyle={TOOLTIP_STYLE}
            formatter={(value, key) => [
              `${toNumber(value)}${unit}`,
              labelFor.get(toKey(key)) ?? toKey(key),
            ]}
          />
          {series.map((s) => (
            <Area
              key={s.key}
              type="monotone"
              dataKey={s.key}
              stackId="1"
              stroke={s.color}
              fill={s.color}
              fillOpacity={0.9}
              {...animation}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
